import Hero from '../components/Hero';
import ServicesSection from '../components/ServicesSection';
import WhyChooseUs from '../components/WhyChooseUs';
import VehicleOrigins from '../components/VehicleOrigins';
import CTASection from '../components/CTASection';
import SEO from '../components/SEO';
import { useTranslation } from 'react-i18next';

export default function Home() {
  const { t } = useTranslation();
  const localBusinessSchema = {
    '@context': 'https://schema.org',
    '@type': 'AutoRepair',
    name: 'Vigor Auto Repair Garage - Ras Al Khor Dubai',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Ras Al Khor Industrial Area',
      addressLocality: 'Ras Al Khor',
      addressRegion: 'Dubai',
      postalCode: '00000',
      addressCountry: 'AE',
    },
  telephone: '+971524333690',
    openingHours: 'Mo-Su 08:00-20:00',
    geo: {
      '@type': 'GeoCoordinates',
      latitude: '25.1835',
      longitude: '55.3667',
    },
    url: 'https://www.vigorauto.ae',
    image: 'https://www.vigorauto.ae/logo3.png',
    priceRange: '$$',
    description:
      'Best car garage in Ras Al Khor Dubai offering expert auto repair, car service, and maintenance. Dealer-alternative workshop for European, Japanese, Korean, and American cars. Serving Ras Al Khor, Nad Al Hamar, Al Awir, and International City with free pickup and dropoff.',
    areaServed: [
      { '@type': 'City', name: 'Dubai' },
      { '@type': 'Place', name: 'Ras Al Khor' },
      { '@type': 'Place', name: 'Nad Al Hamar' },
      { '@type': 'Place', name: 'Al Awir' },
      { '@type': 'Place', name: 'International City' }
    ],
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Auto Repair Services',
      itemListElement: [
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Car Engine Repair' } },
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Transmission Repair' } },
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Car AC Repair & Service' } },
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Brake Service & Repair' } },
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Car Denting & Painting' } },
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Computer Diagnostics' } },
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Major Car Service' } },
        { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Oil Change Service' } }
      ]
    }
  };

  return (
    <>
      <SEO
        title="Best Car Garage in Ras Al Khor Dubai | Vigor Auto Repair - Dealer Alternative Workshop"
        description="Premier car garage in Ras Al Khor Dubai. Expert auto repair and car service for European, Japanese, Korean & American cars. Free pickup/dropoff. Serving Nad Al Hamar, Al Awir & International City. Certified technicians, transparent pricing."
        keywords="car garage in Ras Al Khor, car garage dubai, car repair Ras Al Khor, auto service Ras Al Khor, car service centre Ras Al Khor, car garage Nad Al Hamar, car repair dubai, auto repair dubai, car service dubai, european car service dubai, japanese car service dubai, dealer alternative dubai, car engine repair dubai, car transmission repair dubai, car AC repair dubai, car brake service dubai, car diagnostics dubai, oil change dubai, car garage near me"
        schema={localBusinessSchema}
      />
      <Hero
        backgroundImages={[
          '/images/carservice.jpg',
          '/images/carservice2.png',
          '/images/carinservice.png',
          '/images/Freeinspection.jpg',
        ]}
        autoRotateInterval={3000}
        showArrows={false}
      />
      {/* Free Car Inspection Highlight */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_15%_40%,rgba(245,211,19,0.25),transparent_70%)] pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 py-10 md:py-12">
          <div className="grid lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-7 relative">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#F5D313]/10 border border-[#F5D313]/30 text-xs font-semibold uppercase tracking-wide text-slate-800 mb-5">
                <span className="h-2 w-2 rounded-full bg-[#F5D313] animate-pulse" /> {t('inspection.badge')}
              </div>
              <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900 mb-4">{t('inspection.title')}</h2>
              <p className="text-slate-600 text-sm md:text-base leading-relaxed max-w-xl">
                {t('inspection.description')}
              </p>
              <ul className="mt-5 grid sm:grid-cols-2 gap-2 text-xs md:text-sm text-slate-700">
                {[
                  t('inspection.items.brake'),
                  t('inspection.items.belt'),
                  t('inspection.items.spark'),
                  t('inspection.items.radiator'),
                  t('inspection.items.brakeFluid'),
                  t('inspection.items.acFilter')
                ].map(item => (
                  <li key={item} className="flex items-center gap-2">
                    <span className="text-[#F5D313]">•</span>{item}
                  </li>
                ))}
              </ul>
              <div className="mt-6 flex flex-wrap gap-4">
                <a
                  href="https://wa.me/971524333690?text=I%20would%20like%20to%20book%20the%20Free%20Car%20Inspection"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-lg bg-[#F5D313] px-6 py-3 text-sm font-semibold text-slate-900 shadow hover:bg-[#e4c40f] transition"
                >
                  {t('inspection.cta.book')}
                </a>
                <a
                  href="/offers"
                  className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-6 py-3 text-sm font-medium text-slate-800 hover:bg-slate-100 transition"
                >
                  {t('inspection.cta.offers')}
                </a>
              </div>
            </div>
            <div className="lg:col-span-5 relative">
              <div className="relative h-64 md:h-72 rounded-2xl overflow-hidden border border-slate-200 shadow-sm">
                <img
                  src="/images/Freeinspection.png"
                  alt="Inspection in progress"
                  className="absolute inset-0 w-full h-full object-cover scale-105"
                  loading="lazy"
                  decoding="async"
                />
                <div className="absolute inset-0 bg-gradient-to-tr from-blue-950/60 via-blue-900/20 to-transparent" />
                <div className="absolute bottom-3 left-3 right-3 bg-white/80 backdrop-blur px-4 py-2 rounded-lg flex items-center justify-between text-[11px] text-slate-700 border border-white/60">
                  <span className="font-semibold tracking-wide">Multi‑Point Checklist</span>
                  <span className="inline-flex items-center gap-1 text-slate-900 font-semibold">FREE</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <VehicleOrigins />
      <ServicesSection />
      {/* <ServiceProcess /> */}
      <WhyChooseUs />
      {/* <TestimonialsSection /> */}
      <CTASection
        primaryHref="/request-quote"
        primaryLabel="Request A Quote"
  secondaryHref="https://wa.me/971524333690"
        secondaryLabel="WhatsApp"
      />
    </>
  );
}
